import {
  DEFAULT_PIPELINE_DEFINITION,
  getApprovablePhases,
  getPhaseLabels,
  getPhaseOrder,
  getStartablePhases,
} from '@internal/spassu-code-contract';
import type {
  ActivityWorkPath,
  AIEngineName,
  IssueStateCategory,
  Phase,
  PhaseConformitySummary,
  ProviderId,
} from '@internal/spassu-code-contract';

export type {
  ActivityWorkPath,
  AutoStartPolicy,
  ApprovalPolicy,
  Phase,
  PhaseTransitionRule,
  PipelineDefinition,
  PipelinePhaseDefinition,
  SkillOrigin,
  // Multi-provider SCM/Boards types (Tarefa 5+)
  Issue,
  IssueQuery,
  IssueScope,
  IssueStateCategory,
  IssueSearchResult,
  ProviderId,
  Repository,
  RepositoryBranch,
  RepositoryHierarchy,
  RepositoryOwner,
  RepositoryProject,
} from '@internal/spassu-code-contract';

/**
 * Provider descriptor surfaced by `GET /scm/providers`. Drives the frontend's
 * adaptive picker — when only one is enabled we render it silently.
 */
export interface ScmProviderDescriptor {
  id: import('@internal/spassu-code-contract').ProviderId;
  displayName: string;
  hierarchy: import('@internal/spassu-code-contract').RepositoryHierarchy;
}

/**
 * Projeto gerenciável na tela "Controle de Acesso". Cada item equivale a um
 * `project_key` do RBAC, que segue a MESMA chave que os gates do backend
 * consultam em cada provider:
 *
 * - provider com camada de projeto (Azure DevOps): um item por projeto da
 *   organização; `name` = projeto Azure (`azureProject` da atividade) e
 *   `owner` = organização;
 * - provider flat (GitHub): um item por owner; `name === owner`
 *   (`repoOwner` da atividade).
 *
 * Gravar owner como `project_key` para Azure (comportamento entre 2026-05 e
 * 2026-09) criava memberships que nenhum gate lia.
 */
export interface ManagedProject {
  /** project_key usado em todas as APIs RBAC. */
  name: string;
  /** Owner/organização dona do projeto (igual a `name` em providers flat). */
  owner: string;
  /**
   * O que o `project_key` representa: `project` (camada de projeto dentro do
   * owner, Azure DevOps) ou `owner` (o próprio owner/organização, GitHub).
   * Dirige os rótulos da UI ("Projeto" vs "Organização").
   */
  scope: 'project' | 'owner';
  /** Provider de origem — usado para agrupar/filtrar a UI. */
  providerId: import('@internal/spassu-code-contract').ProviderId;
  providerLabel: string;
}

export type ActivityStatus = 'preparing' | 'active' | 'completed' | 'error';

export type ActivityDeliveryOutcome = 'delivered' | 'closed_without_delivery';

export type CloneStatus = 'pending' | 'cloning' | 'success' | 'failed';

export type ActivityHealthState =
  | 'healthy'
  | 'projecting'
  | 'degraded'
  | 'blocked'
  | 'recovery-needed';

export type ActivityHealthActionCode =
  | 'none'
  | 'run_doctor'
  | 'reconnect_backend'
  | 'retry_sync'
  | 'inspect_projection';

export interface ActivityHealthSignal {
  state: ActivityHealthState;
  reasonCode: string;
  summary: string;
  details?: Record<string, unknown>;
}

export interface ActivityCurrentHealth {
  checkpoint: string;
  localHealth: ActivityHealthSignal;
  remoteHealth: ActivityHealthSignal;
  overallHealth: ActivityHealthSignal;
  actionCode: ActivityHealthActionCode;
  actionText: string | null;
  emittedAt: string;
}

export interface ActivityWorkItem {
  id: number;
  type: string;
  url: string;
  title: string;
  state: string;
}

export interface ActivityIssue {
  externalId: string;
  type: string;
  title: string;
  state: string;
  stateCategory: IssueStateCategory | null;
  url: string;
}

export interface Activity {
  id: string;
  userId: string;
  title: string;
  description: string;
  /** @deprecated Use `repoProject` (will be null for providers without project layer). */
  azureProject: string;
  /** @deprecated Use `repoName`. */
  azureRepository: string;
  branch: string | null;
  clonePath: string | null;
  cloneStatus?: CloneStatus | null;
  cloneError?: string | null;
  clonedAt?: string | null;
  currentPhase: Phase;
  status: ActivityStatus;
  errorMessage?: string | null;
  workPath?: ActivityWorkPath;
  commitsJson?: string | null;
  prUrl?: string | null;
  /** Snapshot do título do PR persistido no `create_pull_request`. */
  prTitle?: string | null;
  /** Snapshot da descrição (markdown) do PR persistido no `create_pull_request`. */
  prDescription?: string | null;
  deliveryOutcome?: ActivityDeliveryOutcome | null;
  /** @deprecated Use `issue`. */
  azureWorkItem?: ActivityWorkItem | null;
  /** Provider-agnostic identification (multi-provider). */
  provider?: string;
  providerExternalId?: string | null;
  repoOwner?: string | null;
  repoProject?: string | null;
  repoName?: string | null;
  issue?: ActivityIssue | null;
  currentHealth?: ActivityCurrentHealth | null;
  /** Evidence summary consumed by the existing CTA in a later rollout. */
  phaseConformity?: PhaseConformitySummary | null;
  /**
   * AI engine usado para executar fases (C5). Default 'claude-code'.
   * Fases skill-dependentes ficam bloqueadas pela UI quando a engine não
   * suporta skills (gate por capability via canEngineNameRunPhase, D2).
   */
  aiEngine?: AIEngineName;
  /**
   * Modelo desta atividade (usar-modelos-worker, RF10): escolhido no Worker
   * Desktop e congelado na criação. NÃO muda depois — trocar o modelo no
   * Desktop afeta apenas atividades novas. Ausente em backend antigo.
   */
  aiModel?: string | null;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string | null;
}

export interface ChatMessage {
  id: string;
  activityId: string;
  phase: Phase;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface PhaseMetrics {
  id: string;
  activityId: string;
  phase: Phase;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  latencyMs: number;
  agentRole: string;
  startedAt: string;
  completedAt: string | null;
}

export interface Artifact {
  id: string;
  activityId: string;
  phase: Phase;
  artifactType: string;
  content: string;
  filePath?: string;
  approved: boolean;
  approvedBy?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  createdAt: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
}

export interface Approach {
  title: string;
  description: string;
  pros: string[];
  cons: string[];
  tradeoffs: string[];
  recommended: boolean;
}

export interface ReviewIssue {
  severity: 'error' | 'warning' | 'info';
  file: string;
  line?: number;
  message: string;
  rule: string;
}

export interface ReviewResult {
  passed: boolean;
  issues: ReviewIssue[];
  summary: string;
}

export interface AzureProject {
  id: string;
  name: string;
  description?: string;
}

export interface AzureRepository {
  id: string;
  name: string;
  defaultBranch?: string;
  remoteUrl?: string;
  webUrl?: string;
}

export interface CreateActivityInput {
  title: string;
  description?: string;
  /** @deprecated Use `repoProject` (set explicitly to `null` for providers without project layer). */
  azureProject?: string;
  /** @deprecated Use `repoName`. */
  azureRepository?: string;
  branch?: string;
  workPath: ActivityWorkPath;
  // Azure Boards Work Item (optional — absent means "created from scratch")
  /** @deprecated Use `issueExternalId`. */
  azureWorkItemId?: number;
  /** @deprecated Use `issueType`. */
  azureWorkItemType?: string;
  /** @deprecated Use `issueUrl`. */
  azureWorkItemUrl?: string;
  /** @deprecated Use `issueTitle`. */
  azureWorkItemTitle?: string;
  /** @deprecated Use `issueState`. */
  azureWorkItemState?: string;
  // Provider-agnostic shape (multi-provider). When `provider` is anything
  // other than 'azure-devops' the agnostic fields are required.
  provider?: ProviderId;
  repoOwner?: string;
  repoProject?: string | null;
  repoName?: string;
  issueExternalId?: string;
  issueType?: string;
  issueTitle?: string;
  issueState?: string;
  issueUrl?: string;
  /** AI engine selecionada (D2 — Workstream C/D). Default 'claude-code' aplicado pelo backend. */
  aiEngine?: AIEngineName;
}

/**
 * Catálogo de engines de IA exposto por GET /ai-engines (DT-06 da iniciativa
 * integracao-github-copilot). `authStatus` reflete a credencial no WORKER do
 * usuário; `state: 'unknown'` quando não há worker conectado ou o worker é
 * anterior ao comando `get_engine_auth_status`.
 */
export interface AiEngineAuthStatusKnown {
  state: 'known';
  authenticated: boolean;
  activeMode: 'subscription' | 'api-key' | 'env' | null;
  /** Motivo + ação esperada quando não autenticada (D-GHC-5). */
  diagnostic?: string;
  /**
   * A execução encontraria um runtime da engine na máquina do worker?
   * (iniciativa disponibilidade-runtime-engines). Ausente = worker anterior
   * ao campo — tratar como desconhecido (fail-open).
   */
  runtimeAvailable?: boolean;
  /** Motivo + ação esperada quando `runtimeAvailable === false`. */
  runtimeDiagnostic?: string;
}

export interface AiEngineAuthStatusUnknown {
  state: 'unknown';
  reason: 'worker_disconnected' | 'worker_outdated';
}

export interface AiEngineCatalogEntry {
  name: AIEngineName;
  capabilities: {
    supportsSkillsPlugin: boolean;
    supportsMCP: boolean;
    supportsToolUse: boolean;
    supportsStreaming: boolean;
  };
  /** false = o worker conectado não anuncia a feature exigida pela engine. */
  supportedByWorker?: boolean;
  authStatus?: AiEngineAuthStatusKnown | AiEngineAuthStatusUnknown;
}

export interface AiEnginesResponse {
  engines: AiEngineCatalogEntry[];
  minimumClaudeVersion: string;
  skillDependentPhases: Phase[];
}

export interface AzureBranch {
  name: string;
  isDefault: boolean;
}

export interface ActivityFilters {
  status?: ActivityStatus;
  azureProject?: string;
  azureRepository?: string;
  page?: number;
  limit?: number;
  mineOnly?: boolean;
  /** Entity ref do criador (ex.: `user:default/fulano`). */
  createdBy?: string;
}

export interface ActivityFilterOptions {
  projects: string[];
  repositories: string[];
  /** Entity refs distintos dos criadores visíveis pela ACL. */
  creators?: string[];
}

export interface DeleteIncompleteActivitiesResult {
  deletedCount: number;
  cloneCleanupErrors: number;
  releasedExecutionModeCount?: number;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const PHASES_ORDER: Phase[] = getPhaseOrder(DEFAULT_PIPELINE_DEFINITION);
export const PHASE_LABELS: Record<Phase, string> = getPhaseLabels(
  DEFAULT_PIPELINE_DEFINITION,
);

export const STATUS_LABELS: Record<ActivityStatus, string> = {
  preparing: 'Preparando',
  active: 'Ativa',
  completed: 'Concluída',
  error: 'Erro',
};

export const APPROVABLE_PHASES: Phase[] = getApprovablePhases(
  DEFAULT_PIPELINE_DEFINITION,
);
export const ELIGIBLE_PHASES: Phase[] = getStartablePhases(
  DEFAULT_PIPELINE_DEFINITION,
);

export interface QuestionOption {
  label: string;
  description: string;
}

export interface Question {
  question: string;
  header: string;
  options: QuestionOption[];
  multiSelect: boolean;
}

export interface AskUserQuestionInput {
  questions: Question[];
}

export type InteractionType = 'session' | 'question' | 'approach';

export type InteractionStatus =
  | 'streaming'
  | 'awaiting_user'
  | 'expired'
  | 'completed'
  | 'canceled';

export type InteractionResumeMode = 'direct' | 'summary_required';

export type ResumeStreamConflictReason =
  | 'no_inflight_stream'
  | 'interaction_mismatch'
  | 'worker_disconnected';

export interface ResumeStreamConflictInteractionState {
  status: InteractionStatus;
  resumeMode: InteractionResumeMode;
  terminalReason: string | null;
}

export interface ResumeStreamConflict {
  error: string;
  reason?: ResumeStreamConflictReason;
  retryable?: boolean;
  interactionState?: ResumeStreamConflictInteractionState;
}

export interface InteractionState {
  id: string;
  activityId: string;
  phase: Phase;
  type: InteractionType;
  status: InteractionStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  resumeMode: InteractionResumeMode;
  resumeWindowEndsAt: string | null;
  resolvedAt: string | null;
  lastRecoveredAt: string | null;
  terminalReason: string | null;
  canResume: boolean;
  recoverySnapshotContent?: string;
  questions?: Question[];
  approaches?: { question: string; approaches: Approach[] };
}

export interface AnswerInteractionResult {
  success: boolean;
  resumeMode: InteractionResumeMode;
  resumeMessage?: string;
}

export type TaskStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'error'
  | 'paused';

export type ExecutionState = 'idle' | 'running' | 'paused';

export interface TaskItem {
  id: string;
  activityId: string;
  taskNumber: string;
  title: string;
  filePath?: string | null;
  dependsOn?: string[];
  status: TaskStatus;
  executionLog: string | null;
  errorMessage: string | null;
  /** Real worker signal: true only after merge/reconciliation confirms integration. */
  integrated?: boolean | null;
  integratedAt?: string | null;
  parallelSlot?: number | null;
  totalSlots?: number | null;
  worktreePath?: string | null;
  /** UI-only timestamp derived from live task events; not persisted by backend. */
  lastEventAt?: string | null;
  /**
   * RF5: heartbeat durável do backend (último evento recebido do worker). Fonte
   * de verdade do "sem atividade", independente do feed SSE ao vivo.
   */
  lastWorkerEventAt?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface WorkerStatus {
  connected: boolean;
  workerVersion?: string;
  connectedAt?: string;
  userId?: string;
  outdated?: boolean;
}

export interface WorkerToken {
  token: string;
  userId: string;
  ttlSeconds: number;
  expiresAt: string;
}

export interface StreamEvent {
  type:
    | 'text'
    | 'tool_use'
    | 'ask_user_question'
    | 'metrics'
    | 'phase_complete'
    | 'phase_auto_transition'
    | 'activity_complete'
    | 'approaches'
    | 'review_started'
    | 'review_result'
    | 'error'
    | 'rate_limit'
    | 'model_fallback'
    | 'done'
    | 'task_started'
    | 'task_progress'
    | 'task_completed'
    | 'task_integrated'
    | 'task_error'
    | 'worktree_lifecycle'
    | 'tasks_discovered'
    | 'execution_complete'
    | 'execution_paused'
    | 'phase_conformity_repair_required'
    | 'phase_conformity_repair_unavailable';
  /** Sequential event ID from SSE `id:` field — used for deterministic replay on reconnect. */
  seqId?: number;
  content: string;
  metrics?: Partial<PhaseMetrics>;
  questions?: AskUserQuestionInput;
  approaches?: { question: string; approaches: Approach[] };
  review?: ReviewResult;
  phase?: string;
  nextPhase?: Phase;
  taskNumber?: string;
  taskTitle?: string;
  taskChunkType?: 'text' | 'tool_use';
  errorMessage?: string;
  integrated?: boolean;
  lifecycleAction?: string;
  branch?: string;
  branchAction?: string;
  reason?: string;
  tasks?: Array<{ taskNumber: string; title: string; dependsOn?: string[] }>;
  parallelSlot?: number;
  totalSlots?: number;
  worktreePath?: string;
  phaseAttemptId?: string;
  missingReasonCodes?: string[];
  missingConcepts?: string[];
  code?: string;
  resetTime?: string;
  timezone?: string;
}

// ─── Skill Vault Types ───

export interface SkillSummary {
  id: string;
  name: string;
  version: string | null;
  description: string | null;
  phase: string | null;
  content: string;
  signature: string | null;
  signedBy: string | null;
  signedAt: string | null;
  status: string;
  origin: string;
  reviewerId: string | null;
  approvedAt: string | null;
  usageCount: number;
  avgQualityScore: number | null;
  avgTokensSaved: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkillEffectivenessEntry {
  id: string;
  name: string;
  phase: string | null;
  usageCount: number;
  avgQualityScore: number | null;
  avgTokensSaved: number | null;
}

// ─── CLI Activity Page Types ───

export type TraceReliabilityState =
  | 'confirmed'
  | 'calculated'
  | 'estimated'
  | 'partial'
  | 'unavailable'
  | 'not-applicable';

export interface TraceReliabilityIndicator {
  state: TraceReliabilityState;
  label: string;
  source: string;
  period: string;
  coverage: string;
  updatedAt: string | null;
  explanation: string;
  currency?: string;
}

// ─── RBAC Types ───

export type Role =
  | 'admin'
  | 'gestor'
  | 'analista'
  | 'desenvolvedor'
  | 'revisor'
  | 'aprovador'
  | 'observador';

export type RbacAction =
  | 'member.add'
  | 'member.remove'
  | 'member.list'
  | 'activity.create'
  | 'activity.read.own'
  | 'activity.read.project'
  | 'activity.delete.own'
  | 'activity.bypass_owner_lock'
  | 'phase.interact'
  | 'phase.approve'
  | 'phase.reject'
  | 'artifact.view'
  | 'artifact.approve'
  | 'artifact.reject'
  | 'workitem.read'
  | 'workitem.write'
  | 'workitem.assign'
  | 'skill.manage'
  | 'skill.review'
  | 'audit.view'
  | 'audit.export';

export interface ProjectMember {
  id: string;
  projectKey: string;
  repositoryKey: string | null;
  entityRef: string;
  entityKind: 'User' | 'Group';
  role: Role;
  assignedBy: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface AccessibleProject {
  projectKey: string;
  /**
   * Provider SCM em que o `projectKey` vale (`'*'` para admin global). O
   * backend agrega memberships de todos os providers, então o mesmo
   * `projectKey` pode aparecer mais de uma vez com providers distintos.
   */
  provider?: string;
  roles: Role[];
  repositories: string[];
  allRepositories: boolean;
  canSeeAllActivities: boolean;
}

export interface AuditLogEntry {
  id: string;
  actorRef: string;
  action: string;
  projectKey: string | null;
  resourceType: string | null;
  resourceId: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogFilters {
  projectKey?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  gestor: 'Gestor de Projeto',
  analista: 'Analista de Requisitos',
  desenvolvedor: 'Desenvolvedor',
  revisor: 'Revisor',
  aprovador: 'Aprovador',
  observador: 'Observador',
};

/**
 * Client-side permission matrix (mirrors backend).
 * Used ONLY for UI conditioning — backend is the source of truth.
 */
export const CLIENT_PERMISSION_MATRIX: Record<RbacAction, Role[]> = {
  'member.add': ['admin', 'gestor'],
  'member.remove': ['admin', 'gestor'],
  'member.list': ['admin', 'gestor', 'observador'],
  'activity.create': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'activity.read.own': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'activity.read.project': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'activity.delete.own': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'activity.bypass_owner_lock': [
    'admin',
    'gestor',
    'analista',
    'revisor',
    'aprovador',
    'observador',
  ],
  'phase.interact': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'phase.approve': ['admin', 'gestor', 'aprovador'],
  'phase.reject': ['admin', 'gestor', 'revisor', 'aprovador'],
  'artifact.view': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'artifact.approve': ['admin', 'revisor', 'aprovador'],
  'artifact.reject': ['admin', 'revisor', 'aprovador'],
  'workitem.read': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'workitem.write': ['admin', 'gestor', 'analista'],
  'workitem.assign': ['admin', 'gestor', 'analista'],
  'skill.manage': ['admin'],
  'skill.review': ['admin', 'gestor', 'revisor'],
  'audit.view': ['admin', 'gestor', 'observador'],
  'audit.export': ['admin', 'gestor'],
};

// ─── Azure Boards — Work Item types ───

export interface AzureWorkItemType {
  name: string;
  referenceName: string;
  description?: string;
  color?: string;
  icon?: string;
  isDisabled?: boolean;
  states?: Array<{ name: string; category: string }>;
}

export interface AzureWorkItemSummary {
  id: number;
  type: string;
  title: string;
  state: string;
  assignedTo?: string;
  areaPath?: string;
  iterationPath?: string;
  tags?: string[];
  url: string;
  parentId?: number;
}

export interface AzureWorkItemDetail extends AzureWorkItemSummary {
  description?: string;
  descriptionText?: string;
  acceptanceCriteria?: string;
  acceptanceCriteriaText?: string;
  reproSteps?: string;
  reproStepsText?: string;
  rev: number;
  parent?: { id: number; type: string; title: string };
}

export interface AzureWorkItemSnapshot {
  description?: string;
  acceptanceCriteria?: string;
  reproSteps?: string;
  tags?: string[];
  areaPath?: string;
  iterationPath?: string;
  assignedTo?: string;
  parent?: { id: number; type: string; title: string };
  capturedAt: string;
  rev: number;
}

export interface SearchWorkItemsFilters {
  projectId: string;
  types?: string[];
  states?: string[];
  assignedToMe?: boolean;
  assignedToRef?: string;
  text?: string;
  workItemId?: number;
  areaPath?: string;
  iterationPath?: string;
  top?: number;
  skip?: number;
}

export interface PaginatedWorkItems {
  data: AzureWorkItemSummary[];
  total: number;
  top: number;
  skip: number;
  hasMore: boolean;
}
